/**
 * Fixtures do modo mock do dashboard (campanhas de marketing).
 *
 * Ative com REACT_APP_MOCK=true (ver src/mocks/install.js). Este arquivo é a
 * ÚNICA coisa que você precisa editar para gerar uma nova captura com números
 * diferentes: valores monetários estão em CENTAVOS (o front divide por 100).
 *
 * Nada aqui roda em produção — só quando REACT_APP_MOCK === 'true'.
 */

import { USER_PHOTO } from "./userPhoto";

// Helper: reais -> centavos, pra manter a edição legível.
const brl = reais => Math.round(reais * 100);

// ---------------------------------------------------------------------------
// Marca / sessão (semeada no localStorage antes do React montar)
// ---------------------------------------------------------------------------

// Todas as permissões usadas pelo menu lateral, pra renderizar o sidebar cheio.
const ALL_PERMISSIONS = [
    "view_unrelated_dashboard", "view_related_dashboard",
    "view_product", "view_color", "view_category", "view_tag", "view_customization",
    "view_unrelated_budget", "view_related_budget",
    "view_unrelated_order", "view_related_order",
    "view_unrelated_customer", "view_related_customer",
    "view_integration", "content", "admin", "role",
    "calculation", "status", "email_template", "edit_category", "configure_domain",
    "webhook", "external_api",
].map((name, id) => ({ id: id + 1, name }));

export const MOCK_USER = {
    id: 1,
    name: "Fernanda Borelli",
    email: "fernanda@empresa.com.br",
    photo_url: USER_PHOTO,
    role: {
        id: 1,
        name: "Administrador",
        permissions: ALL_PERMISSIONS,
    },
};

export const MOCK_PLAN = {
    id: 1,
    name: "Plano Pro",
    features: [
        { value: "crm" },
        { value: "webhook" },
        { value: "external_api" },
    ],
    themes: [],
};

export const MOCK_THEME = { id: 1, name: "Padrão" };

// ---------------------------------------------------------------------------
// Status (endpoint /status) — reutilizados pelo gráfico de rosca
// ---------------------------------------------------------------------------

export const BUDGET_STATUSES = [
    { id: 11, name: "Aguardando orçamento", type: "budget", color: "#6C4CE0" },
    { id: 12, name: "Orçamento enviado", type: "budget", color: "#4C7DE0" },
    { id: 13, name: "Em negociação", type: "budget", color: "#E0A33C" },
    { id: 14, name: "Aprovado", type: "budget", color: "#2FBF8F" },
    { id: 15, name: "Recusado", type: "budget", color: "#E05B6B" },
];

export const ORDER_STATUSES = [
    { id: 21, name: "Aguardando pagamento", type: "order", color: "#E0A33C" },
    { id: 22, name: "Em produção", type: "order", color: "#6C4CE0" },
    { id: 23, name: "Em transporte", type: "order", color: "#4C7DE0" },
    { id: 24, name: "Entregue", type: "order", color: "#2FBF8F" },
    { id: 25, name: "Cancelado", type: "order", color: "#E05B6B" },
];

// ---------------------------------------------------------------------------
// Vendedores
// ---------------------------------------------------------------------------

const SELLERS = [
    { id: 1, name: "Marina Souza" },
    { id: 2, name: "Carlos Mendes" },
    { id: 3, name: "Ana Beatriz Costa" },
    { id: 4, name: "Rafael Almeida" },
    { id: 5, name: "Juliana Ribeiro" },
    { id: 6, name: "Pedro Henrique Lima" },
];

const sellerPerformance = [
    { id: 1, budgets: 96, budgetAmount: brl(438920), orders: 41, orderAmount: brl(198340), conv: 42.7, resp: 28, pending: 3 },
    { id: 2, budgets: 84, budgetAmount: brl(372150), orders: 32, orderAmount: brl(151280), conv: 38.1, resp: 35, pending: 4 },
    { id: 3, budgets: 78, budgetAmount: brl(341600), orders: 29, orderAmount: brl(129870), conv: 37.2, resp: 44, pending: 2 },
    { id: 4, budgets: 61, budgetAmount: brl(268940), orders: 21, orderAmount: brl(92450), conv: 34.4, resp: 51, pending: 5 },
    { id: 5, budgets: 58, budgetAmount: brl(245730), orders: 18, orderAmount: brl(78120), conv: 31.0, resp: 39, pending: 3 },
    { id: 6, budgets: 35, budgetAmount: brl(174960), orders: 9, orderAmount: brl(37390), conv: 25.7, resp: 62, pending: 1 },
].map(s => {
    const seller = SELLERS.find(x => x.id === s.id);
    return {
        admin: { id: seller.id, name: seller.name, photo_url: null },
        budget_count: s.budgets,
        budget_amount: s.budgetAmount,
        order_count: s.orders,
        order_amount: s.orderAmount,
        conversion_rate: s.conv,
        average_response_time: s.resp,
        pending_budgets: s.pending,
    };
});

// ---------------------------------------------------------------------------
// Produtos (rankings)
// ---------------------------------------------------------------------------

const mostBudgeted = [
    { id: 101, sku: "BM-1042", name: "Squeeze Térmico Inox 500ml", count: 87 },
    { id: 102, sku: "BM-2087", name: "Caneca de Porcelana Personalizada", count: 74 },
    { id: 103, sku: "BM-3391", name: "Ecobag Algodão Cru", count: 66 },
    { id: 104, sku: "BM-5510", name: "Power Bank 10.000mAh", count: 58 },
    { id: 105, sku: "BM-4420", name: "Caderno Capa Dura A5", count: 49 },
    { id: 106, sku: "BM-6712", name: "Kit Gourmet Churrasco", count: 41 },
    { id: 107, sku: "BM-7801", name: "Garrafa de Vidro com Luva", count: 37 },
    { id: 108, sku: "BM-8123", name: "Mochila Executiva Antifurto", count: 33 },
];

const mostOrdered = [
    { id: 101, sku: "BM-1042", name: "Squeeze Térmico Inox 500ml", count: 34 },
    { id: 102, sku: "BM-2087", name: "Caneca de Porcelana Personalizada", count: 28 },
    { id: 104, sku: "BM-5510", name: "Power Bank 10.000mAh", count: 22 },
    { id: 103, sku: "BM-3391", name: "Ecobag Algodão Cru", count: 19 },
    { id: 106, sku: "BM-6712", name: "Kit Gourmet Churrasco", count: 16 },
    { id: 105, sku: "BM-4420", name: "Caderno Capa Dura A5", count: 13 },
    { id: 108, sku: "BM-8123", name: "Mochila Executiva Antifurto", count: 11 },
    { id: 107, sku: "BM-7801", name: "Garrafa de Vidro com Luva", count: 8 },
];

const lowConversion = [
    { id: 109, sku: "BM-9044", name: "Fone Bluetooth TWS", budget_count: 52, order_count: 6, conversion_rate: 11.5 },
    { id: 110, sku: "BM-9210", name: "Relógio de Mesa Digital", budget_count: 38, order_count: 5, conversion_rate: 13.2 },
    { id: 111, sku: "BM-9377", name: "Kit Escritório Premium", budget_count: 44, order_count: 7, conversion_rate: 15.9 },
    { id: 112, sku: "BM-9502", name: "Guarda-chuva Automático", budget_count: 29, order_count: 5, conversion_rate: 17.2 },
];

// ---------------------------------------------------------------------------
// Distribuição por status e evolução mensal
// ---------------------------------------------------------------------------

const budgetByStatus = [
    { status: BUDGET_STATUSES[0], total: 84, amount: brl(372150) },
    { status: BUDGET_STATUSES[1], total: 128, amount: brl(561240) },
    { status: BUDGET_STATUSES[2], total: 96, amount: brl(438920) },
    { status: BUDGET_STATUSES[3], total: 74, amount: brl(329870) },
    { status: BUDGET_STATUSES[4], total: 30, amount: brl(140120) },
];

const orderByStatus = [
    { status: ORDER_STATUSES[0], total: 22, amount: brl(98340) },
    { status: ORDER_STATUSES[1], total: 41, amount: brl(187650) },
    { status: ORDER_STATUSES[2], total: 34, amount: brl(154280) },
    { status: ORDER_STATUSES[3], total: 47, amount: brl(212970) },
    { status: ORDER_STATUSES[4], total: 6, amount: brl(34210) },
];

const buildByStatus = rows => ({
    statuses: rows.map(r => ({
        status: r.status,
        total_resources: r.total,
        amount_resources: r.amount,
        margem_real: Math.round(r.amount * 0.32),
        margem_orcamento: Math.round(r.amount * 0.35),
    })),
    totals: {
        count: rows.reduce((s, r) => s + r.total, 0),
        amount: rows.reduce((s, r) => s + r.amount, 0),
        margem_real: rows.reduce((s, r) => s + Math.round(r.amount * 0.32), 0),
        margem_orcamento: rows.reduce((s, r) => s + Math.round(r.amount * 0.35), 0),
    },
});

// Evolução mensal (últimos 7 meses, tendência de alta).
const budgetByMonth = [
    { year: 2026, month: 1, count: 214 },
    { year: 2026, month: 2, count: 248 },
    { year: 2026, month: 3, count: 271 },
    { year: 2026, month: 4, count: 305 },
    { year: 2026, month: 5, count: 342 },
    { year: 2026, month: 6, count: 389 },
    { year: 2026, month: 7, count: 412 },
];

const orderByMonth = [
    { year: 2026, month: 1, count: 71 },
    { year: 2026, month: 2, count: 83 },
    { year: 2026, month: 3, count: 96 },
    { year: 2026, month: 4, count: 108 },
    { year: 2026, month: 5, count: 124 },
    { year: 2026, month: 6, count: 137 },
    { year: 2026, month: 7, count: 150 },
];

const resourceBlock = (byStatus, byMonth) => ({
    amount_resources: byStatus.totals.amount,
    total_items: byStatus.totals.count * 3,
    total_resources: byStatus.totals.count,
    average_ticket: Math.round(byStatus.totals.amount / byStatus.totals.count),
    average_items: 3,
    resources_by_status: byStatus,
    resources_by_admin: [],
    resources_by_month: byMonth,
    items: [],
});

// ---------------------------------------------------------------------------
// Payload final de /dashboard
// ---------------------------------------------------------------------------

const budgetStatusData = buildByStatus(budgetByStatus);
const orderStatusData = buildByStatus(orderByStatus);

const budgetCount = budgetStatusData.totals.count;
const orderCount = orderStatusData.totals.count;
const budgetAmount = budgetStatusData.totals.amount;
const orderAmount = orderStatusData.totals.amount;
const proposalsCount = 328;
const emailSentCount = 296;

export const DASHBOARD_RESPONSE = {
    admins: SELLERS.map(s => ({ id: s.id, name: s.name })),
    budget: resourceBlock(budgetStatusData, budgetByMonth),
    order: resourceBlock(orderStatusData, orderByMonth),
    overview: {
        period: { start_date: "2026-07-01", final_date: "2026-07-31" },
        summary: {
            budget_amount: budgetAmount,
            order_amount: orderAmount,
            conversion_rate: 36.4,
            average_ticket: Math.round(orderAmount / orderCount),
            pending_budgets: 18,
            average_response_time: 42,
        },
        funnel: [
            {
                key: "budgets",
                label: "Orçamentos recebidos",
                count: budgetCount,
                amount: budgetAmount,
                conversion_rate: 100,
            },
            {
                key: "proposals",
                label: "Orçamentos com proposta gerada",
                count: proposalsCount,
                email_sent_count: emailSentCount,
                amount: null,
                conversion_rate: Math.round((proposalsCount / budgetCount) * 1000) / 10,
            },
            {
                key: "orders",
                label: "Pedidos fechados",
                count: orderCount,
                amount: orderAmount,
                conversion_rate: Math.round((orderCount / budgetCount) * 1000) / 10,
            },
        ],
        alerts: [
            { intent: "danger", icon: "clock", title: "Orçamentos há mais de 48h sem proposta", value: 7, description: "Fila que merece resposta antes de novos contatos esfriarem.", to: "/quote/list" },
            { intent: "warning", icon: "quote", title: "Orçamentos sem proposta", value: 18, description: "Solicitações criadas no período e ainda sem proposta gerada.", to: "/quote/list" },
            { intent: "neutral", icon: "send", title: "Orçamentos com proposta gerada hoje", value: 12, description: "Orçamentos com proposta gerada no dia atual.", to: "/quote/list" },
            { intent: "neutral", icon: "order", title: "Pedidos criados hoje", value: 6, description: "Pedidos registrados hoje no sistema.", to: "/order/list" },
            { intent: "warning", icon: "people", title: "Maior fila sem proposta", value: 5, description: "Rafael Almeida", to: "/quote/list" },
            { intent: "warning", icon: "product", title: "Produto com baixa conversão", value: 52, description: "BM-9044 - Fone Bluetooth TWS", to: "/product/list" },
        ],
        seller_performance: sellerPerformance,
        product_insights: {
            most_budgeted: mostBudgeted,
            most_ordered: mostOrdered,
            low_conversion: lowConversion,
        },
    },
};

export const STATUS_RESPONSE = { resources: [...BUDGET_STATUSES, ...ORDER_STATUSES] };

export const BUDGET_COUNT_RESPONSE = { count: 18 };
export const MENTION_NOTIFICATIONS_RESPONSE = { resources: [], unread_count: 0 };
